import { useState, useEffect } from "react";
import { Dialog } from "@remo-co/ui-core/src/components/Dialog";
import { DialogContent } from "@remo-co/ui-core/src/components/DialogContent";
import { DialogTitle } from "@remo-co/ui-core/src/components/DialogTitle";
import { IconButton } from "@remo-co/ui-core/src/components/IconButton";
import { Tooltip } from "@remo-co/ui-core/src/components/Tooltip";
import moment from "moment";
import { Close } from "@remo-co/ui-core/src/icons/Close";
import { useI18n } from "i18n";
import { getInviteUrl } from "helpers/urlHelper";
import { IEvent } from "modules/event/types";
import { selectIsMobile } from "modules/deviceInfo";
import { useSelector } from "react-redux";
import { Typography } from "@remo-co/ui-core/src/components/Typography";
import {
  getGoogleCalendarLink,
  getICSCalendarLink,
  getYahooCalendarLink,
} from "../../services/addToCalendar";
import { useStyles } from "./styles";

interface Props {
  event: IEvent;
  onClose: () => void;
  open?: boolean;
}

export const AddToCalendar = ({ event, open = false, onClose }: Props) => {
  const [inviteLink, setInviteLink] = useState("");
  const { t } = useI18n(["eventLanding"]);
  const isMobileView = useSelector(selectIsMobile);
  const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  const styles = useStyles();
  const startDate = moment(event.startTime);
  const startDateStr = startDate.format("YYYYMMDDTHHmmss");
  const endDate = moment(event.endTime);
  const endDateStr = endDate.format("YYYYMMDDTHHmmss");

  // Copied from https://jasonsalzman.github.io/react-add-to-calendar/
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const onCalClick = (e: any) => {
    e.stopPropagation();
    e.preventDefault();

    let url = e.currentTarget.getAttribute("href");

    if (isMobileView) {
      window.open(url, "_blank", "noopener");

      return;
    }

    url = url.replace(/begin/gi, "BEGIN");

    if (url.startsWith("data") || url.startsWith("BEGIN")) {
      const filename = "download.ics";
      const blob = new Blob([url], {
        type: "text/calendar;charset=utf-8",
      });

      /** **************************************************************
        // many browsers do not properly support downloading data URIs
        // (even with 'download' attribute in use) so this solution
        // ensures the event will download cross-browser
        *************************************************************** */
      const link = document.createElement("a");

      link.href = window.URL.createObjectURL(blob);
      link.setAttribute("download", filename);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  const handleClose = () => {
    onClose();
  };

  useEffect(() => {
    const updateInviteURL = async () => {
      const inviteUrl = await getInviteUrl(event);

      setInviteLink(inviteUrl);
    };

    if (event) {
      updateInviteURL();
    }
  }, [event]);

  const googleCalendarLink = inviteLink
    ? getGoogleCalendarLink(
        event,
        inviteLink,
        startDateStr,
        endDateStr,
        timezone,
      )
    : "";
  const yahooCalendarLink = inviteLink
    ? getYahooCalendarLink(event, inviteLink, startDateStr, endDateStr)
    : "";

  const icsCalendarLink = inviteLink
    ? getICSCalendarLink(
        event,
        inviteLink,
        startDateStr,
        endDateStr,
        isMobileView,
      )
    : "";

  return (
    <Dialog
      open={open}
      fullWidth
      maxWidth="md"
      className={styles.addToCalendarModal}
      data-testid="add-to-calendar-modal"
    >
      <DialogTitle>
        <Typography variant="h3">
          {t("eventLanding:select.calendar")}
        </Typography>
      </DialogTitle>
      <IconButton
        data-testid="add-to-calendar-close"
        onClick={handleClose}
        size="large"
        className={styles.closeButton}
      >
        <Close />
      </IconButton>
      <DialogContent>
        <Tooltip
          title={t("eventLanding:add.google.calendar") as string}
          placement="top"
        >
          <a
            className={`${styles.calLink} ${styles.google}`}
            rel="noopener noreferrer"
            target="_blank"
            href={googleCalendarLink}
            data-testid="google-cal-link"
          >
            {t("eventLanding:google.calendar")}
          </a>
        </Tooltip>
        <Tooltip
          title={t("eventLanding:add.outlook.title") as string}
          placement="top"
        >
          <a
            className={`${styles.calLink} ${styles.outlook}`}
            onClick={onCalClick}
            href={icsCalendarLink}
            data-testid="outlook-cal-link"
          >
            {t("eventLanding:microsoft.outlook")}
          </a>
        </Tooltip>
        <Tooltip
          title={t("eventLanding:add.yahoo.calendar") as string}
          placement="top"
        >
          <a
            className={`${styles.calLink} ${styles.yahoo}`}
            rel="noopener noreferrer"
            target="_blank"
            href={yahooCalendarLink}
            data-testid="yahoo-cal-link"
          >
            {t("eventLanding:yahoo.calendar")}
          </a>
        </Tooltip>
        <Tooltip title={t("eventLanding:add.icall") as string} placement="top">
          <a
            className={`${styles.calLink} ${styles.iCal}`}
            onClick={onCalClick}
            href={icsCalendarLink}
            data-testid="ical-cal-link"
          >
            {t("eventLanding:ical")}
          </a>
        </Tooltip>
      </DialogContent>
    </Dialog>
  );
};
